package fr.frux.shared.presentation

import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.SeasonPhase
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.domain.model.seasonPhase
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Mapping [TreeStatus] (domaine) vers [FruxStatus] (design system), factorisé (#87). Chaque
 * feature (map, tree-list, tree-detail, harvest-detail, harvest-create, tree-add) portait sa
 * propre copie du même `when`, faute de pouvoir dépendre d'une autre feature (règle « no
 * horizontal dependencies »). Vit dans `fr.frux.shared.presentation`, pas dans core-designsystem.
 * Un module core ne peut jamais dépendre de `:shared` (CLAUDE.md règle 4), donc ne peut pas voir
 * [TreeStatus]. `:shared` en revanche dépend déjà d'autres modules core (core-maps,
 * core-network...). Ajouter core-designsystem à cette liste ne viole aucune règle de
 * `checkModuleBoundaries`, qui n'interdit que `:shared -> feature` et `:shared -> composeApp`.
 */
fun TreeStatus.toFruxStatus(): FruxStatus =
    when (this) {
        TreeStatus.FLOWERING -> FruxStatus.Flowering
        TreeStatus.RIPE_READY -> FruxStatus.Available
        TreeStatus.PICKED_CLEAN -> FruxStatus.Picked
        TreeStatus.UNAVAILABLE -> FruxStatus.Unavailable
        TreeStatus.UNKNOWN -> FruxStatus.Unknown
    }

/**
 * Durée de crédibilité d'une observation de terrain, par statut (#604, resserre les 90 jours
 * uniformes de #444). Un fruit mûr ne le reste pas trois mois : au-delà, le badge ment et envoie
 * quelqu'un devant un arbre vide. Distincte de `RECENT_WINDOW` de `TreeSighting.kt`, qui juge la
 * fraîcheur d'un passage et non la validité d'un statut. `null` pour un statut qui ne se périme
 * pas par la date.
 */
fun TreeStatus.observationValidity(): Duration? =
    when (this) {
        TreeStatus.RIPE_READY -> 28.days
        TreeStatus.PICKED_CLEAN -> 21.days
        TreeStatus.FLOWERING -> 42.days
        TreeStatus.UNAVAILABLE, TreeStatus.UNKNOWN -> null
    }

/**
 * Vrai quand un statut de terrain (`FLOWERING`, `RIPE_READY`, `PICKED_CLEAN`) n'est plus crédible :
 * le calendrier existe et répond hors saison au mois courant, ou l'observation qui l'a posé date de
 * plus que sa [observationValidity]. Sans date d'observation, seule la règle du calendrier
 * s'applique. Toujours faux pour `UNAVAILABLE` et `UNKNOWN`.
 *
 * `lastObservedAt` est bien la date du statut : seule une observation de statut par un pair
 * l'avance (ADR 0025), un trait ne la touche pas.
 */
@OptIn(ExperimentalTime::class)
fun isFieldStatusStale(
    status: TreeStatus,
    harvests: List<HarvestWindow>,
    currentMonth: Int,
    lastObservedAt: Instant?,
    now: Instant,
): Boolean {
    val validity = status.observationValidity() ?: return false
    val offSeason = harvests.isNotEmpty() && harvests.seasonPhase(currentMonth) == SeasonPhase.OFF_SEASON
    val tooOld = lastObservedAt != null && now - lastObservedAt > validity
    return offSeason || tooOld
}

/**
 * Résolution du statut affiché (#444, #604).
 *
 * `UNAVAILABLE` s'affiche toujours tel quel : posé par l'opérateur, il n'est jamais périmé.
 *
 * `UNKNOWN` se dérive du calendrier au mois courant, comme avant.
 *
 * Un statut de terrain s'affiche tel quel tant qu'il n'est pas périmé ([isFieldStatusStale]).
 * Périmé, on retombe sur la dérivation calendrier.
 */
@OptIn(ExperimentalTime::class)
fun resolveTreeStatus(
    status: TreeStatus,
    harvests: List<HarvestWindow>,
    currentMonth: Int,
    lastObservedAt: Instant?,
    now: Instant,
): FruxStatus {
    val fromCalendar: () -> FruxStatus = {
        if (harvests.isEmpty()) {
            FruxStatus.Unknown
        } else {
            when (harvests.seasonPhase(currentMonth)) {
                SeasonPhase.IN_SEASON -> FruxStatus.Available
                SeasonPhase.SOON -> FruxStatus.Flowering
                SeasonPhase.OFF_SEASON -> FruxStatus.OffSeason
            }
        }
    }

    return when (status) {
        TreeStatus.UNAVAILABLE -> {
            status.toFruxStatus()
        }

        TreeStatus.UNKNOWN -> {
            fromCalendar()
        }

        TreeStatus.FLOWERING, TreeStatus.RIPE_READY, TreeStatus.PICKED_CLEAN -> {
            if (isFieldStatusStale(status, harvests, currentMonth, lastObservedAt, now)) {
                fromCalendar()
            } else {
                status.toFruxStatus()
            }
        }
    }
}
